import { EmailContext, ThreadMessage } from "../types";
import { authoredTextFromBodyHtml } from "./quoteBoundary";

/* global Office */

// Builds an `EmailContext` shaped exactly like the Chrome extension's, but
// sourced from Office.js (`Office.context.mailbox.item`) instead of DOM scraping.

type Listener = (ctx: EmailContext | null) => void;

const listeners = new Set<Listener>();
let currentItemId: string | null = null;
let lastEmittedContext: EmailContext | null = null;
let pollHandle: ReturnType<typeof setInterval> | null = null;
let lastDraftSnapshot = "";

function isComposeItem(item: Office.MessageRead | Office.MessageCompose | undefined): item is Office.MessageCompose {
  // Compose-mode items expose `body.setAsync` and `subject` as a setter object.
  if (!item) return false;
  return typeof (item as Office.MessageCompose).body?.setAsync === "function";
}

function getAsync<T>(fn: (cb: (result: Office.AsyncResult<T>) => void) => void): Promise<T> {
  return new Promise((resolve) => {
    try {
      fn((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          resolve(undefined as unknown as T);
        }
      });
    } catch {
      resolve(undefined as unknown as T);
    }
  });
}

// Outlook auto-populates a Reply compose body with the quoted previous thread
// (subject line, "From: / Sent: / To: / Subject:" header block, then the
// original message). To detect whether the user has typed anything *new*, we
// have to strip that quoted portion. The markers Outlook uses vary by platform
// and version, so we try several:
//   - Plain text:    "From: ..." header lines, "-----Original Message-----"
//   - Underline rule: "________________________________"
//   - Date stamp:    "On <date>, <name> wrote:"
// Everything from the first marker onwards is treated as quoted.
function stripQuotedReply(text: string): string {
  if (!text) return "";

  const patterns: RegExp[] = [
    /-----+\s*Original Message\s*-----+/i,
    /________________________+/,
    /^\s*From:\s.+$/m,
    /^\s*On\s.+?wrote:\s*$/im,
    /^\s*Le\s.+?a écrit\s*:\s*$/im, // French clients
    /^\s*Am\s.+?schrieb\s.+?:\s*$/im, // German clients
  ];

  let earliestIdx = -1;
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m.index !== undefined) {
      if (earliestIdx === -1 || m.index < earliestIdx) {
        earliestIdx = m.index;
      }
    }
  }

  return earliestIdx >= 0 ? text.slice(0, earliestIdx) : text;
}

function getEmailAddressDetails(field: unknown): { name: string; email: string } {
  if (!field) return { name: "", email: "" };
  const f = field as { displayName?: string; emailAddress?: string };
  return {
    name: f.displayName ?? "",
    email: f.emailAddress ?? "",
  };
}

async function readModeContext(item: Office.MessageRead): Promise<EmailContext> {
  const subject = item.subject ?? "";
  const from = getEmailAddressDetails(item.from);
  const recipients = (item.to ?? []).map((r) =>
    getEmailAddressDetails(r as unknown),
  );
  const recipientName = recipients[0]?.name ?? "";

  const body = (await getAsync<string>((cb) =>
    item.body.getAsync(Office.CoercionType.Text, cb),
  )) ?? "";

  const message: ThreadMessage = {
    sender: from.name,
    senderEmail: from.email,
    body,
    receivedAt: item.dateTimeCreated?.toISOString?.() ?? new Date().toISOString(),
  };

  return {
    messageId: item.itemId ?? null,
    conversationId: item.conversationId ?? undefined,
    subject,
    body,
    sender: from.name,
    senderEmail: from.email,
    userName: Office.context.mailbox?.userProfile?.displayName ?? "",
    userEmail: Office.context.mailbox?.userProfile?.emailAddress ?? "",
    receivedAt: message.receivedAt,
    threadLength: 1,
    recipientName,
    opportunityLabel: "Active opportunity",
    engagementLabel: "Moderately engaged",
    messages: [message],
    isCompose: false,
    hasDraft: false,
    isReply: false,
  };
}

async function composeModeContext(item: Office.MessageCompose): Promise<EmailContext> {
  const [subject, body, bodyHtml, to, from] = await Promise.all([
    getAsync<string>((cb) => item.subject.getAsync(cb)),
    getAsync<string>((cb) => item.body.getAsync(Office.CoercionType.Text, cb)),
    getAsync<string>((cb) => item.body.getAsync(Office.CoercionType.Html, cb)),
    getAsync<Office.EmailAddressDetails[]>((cb) => item.to.getAsync(cb)),
    // `from.getAsync` only exists on compose items where the user can change
    // the From address; otherwise fall back to the user profile.
    getAsync<Office.EmailAddressDetails>((cb) => {
      const f = (item as unknown as { from?: { getAsync?: (cb: unknown) => void } }).from;
      if (f && typeof f.getAsync === "function") {
        f.getAsync(cb as unknown as Parameters<typeof f.getAsync>[0]);
      } else {
        cb({
          status: Office.AsyncResultStatus.Succeeded,
          value: {
            displayName: Office.context.mailbox?.userProfile?.displayName ?? "",
            emailAddress: Office.context.mailbox?.userProfile?.emailAddress ?? "",
          } as Office.EmailAddressDetails,
          error: undefined as unknown as Office.Error,
          asyncContext: undefined,
        } as Office.AsyncResult<Office.EmailAddressDetails>);
      }
    }),
  ]);

  const safeSubject = subject ?? "";
  const safeBody = body ?? "";
  const recipient = to?.[0];
  const recipientName = recipient?.displayName ?? "";

  // Outlook pre-fills the reply body with the quoted prior thread. Strip that
  // before deciding hasDraft — otherwise the moment the user clicks Reply All
  // we'd consider the empty draft "ready to analyse", and the quoted thread
  // would leak into the "Original" view and into scoring.
  //
  // Prefer the HTML-based strip (reliable quote markers); fall back to the
  // plain-text heuristic only when the HTML yields no detectable boundary
  // (e.g. a brand-new email, where the whole body is authored anyway).
  // Fall back to the plain-text heuristic when the HTML extractor finds no quote
  // marker (null) AND when it returns an empty string — on some classic-desktop
  // layouts the marker is found but authored text extraction can still come back
  // empty, and we don't want a `??` (null-only) check to skip the fallback.
  const authoredFromHtml = authoredTextFromBodyHtml(bodyHtml ?? "");
  const userTyped = (authoredFromHtml || stripQuotedReply(safeBody)).trim();
  const hasDraft = userTyped.length > 0;
  const isReply = hasDraft && (item.conversationId ?? "").length > 0;

  // `itemId` is read-only on MessageRead but only available on saved compose
  // items, so it's not declared on MessageCompose. Cast to read it when present.
  const composeItemId = (item as unknown as { itemId?: string }).itemId ?? null;

  return {
    messageId: composeItemId,
    conversationId: item.conversationId ?? undefined,
    subject: safeSubject,
    body: safeBody, // when no read context exists, draft body doubles as thread body
    sender: from?.displayName ?? "",
    senderEmail: from?.emailAddress ?? "",
    userName: Office.context.mailbox?.userProfile?.displayName ?? "",
    userEmail: Office.context.mailbox?.userProfile?.emailAddress ?? "",
    receivedAt: new Date().toISOString(),
    threadLength: 0,
    recipientName,
    opportunityLabel: "",
    engagementLabel: "",
    isCompose: true,
    hasDraft,
    isReply,
    draftSubject: safeSubject,
    draftBody: userTyped || safeBody,
  };
}

/**
 * Read the currently selected mailbox item. Returns null when no message is
 * selected (e.g. the user is viewing the inbox list).
 */
export async function getMailboxContext(): Promise<EmailContext | null> {
  const item = Office.context.mailbox?.item as
    | Office.MessageRead
    | Office.MessageCompose
    | undefined;
  if (!item) return null;

  if (isComposeItem(item)) {
    return composeModeContext(item);
  }
  return readModeContext(item as Office.MessageRead);
}

function notify(ctx: EmailContext | null) {
  lastEmittedContext = ctx;
  listeners.forEach((l) => {
    try { l(ctx); } catch { /* listener error — ignore */ }
  });
}

async function emitCurrent() {
  const ctx = await getMailboxContext();
  currentItemId = ctx?.messageId ?? null;
  lastDraftSnapshot = `${ctx?.draftSubject ?? ""}::${ctx?.draftBody ?? ""}`;
  notify(ctx);
}

async function pollForDraftChanges() {
  const ctx = await getMailboxContext();
  if (!ctx) return;
  const snapshot = `${ctx.draftSubject ?? ""}::${ctx.draftBody ?? ""}`;
  if (snapshot !== lastDraftSnapshot) {
    lastDraftSnapshot = snapshot;
    notify(ctx);
  }
}

/**
 * Subscribe to mailbox context changes. Fires once immediately with the
 * current item, then again whenever the user switches messages or edits
 * the open compose draft (debounced via the 1.2s poll).
 */
export function onContextChange(listener: Listener): () => void {
  listeners.add(listener);

  if (lastEmittedContext !== null) {
    listener(lastEmittedContext);
  } else {
    void emitCurrent();
  }

  return () => {
    listeners.delete(listener);
  };
}

let initialized = false;

/** Wires Office.js handlers. Call once after Office.onReady. */
export function initMailboxContext(): void {
  if (initialized) return;
  initialized = true;

  // Fired when the user clicks a different email in the list (read mode pinned
  // task panes only). Harmless to register in compose mode — it just never fires.
  try {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      () => {
        void emitCurrent();
      },
    );
  } catch {
    /* not supported on this host — fall back to polling */
  }

  // Poll for draft edits. The Office.js API has no granular "body changed"
  // event on compose, so polling at ~1.2s matches the chrome extension's
  // 800ms debounce closely enough.
  if (!pollHandle) {
    pollHandle = setInterval(() => {
      const item = Office.context.mailbox?.item;
      if (!item) return;
      if (isComposeItem(item as Office.MessageCompose)) {
        void pollForDraftChanges();
      } else if ((item as Office.MessageRead).itemId !== currentItemId) {
        void emitCurrent();
      }
    }, 1200);
  }

  void emitCurrent();
}
